import React, {useEffect, useState, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Icon from 'react-web-vector-icons';
import searchIcon from '../../assets/search-glass.png';
import PageBase from "../../components/PageBase";
import Datatable from "../../components/Datatable";
import * as heatmapActions from "../../redux/Heatmap/actions";
import Filters from "../../components/Filters";
import availableFilters from './availableFilters.js';
import LoadingPanel from "../../components/LoadingPanel";
import constants from "../../utils/constants.js";
import columns from './column.js';
import moment from 'moment';
import Button from "../../components/Button";
import PopupWin from "../../components/PopupWin";
import PopupButton from "../../components/PopupButton";
import EditHeatmap from "./EditHeatmap";
import usePersistentFilters from '../../hooks/usePersistentFilters';
import useColumnFilters from '../../hooks/useColumnFilters';
import { getDisplayFields, getDefaultFields } from '../../utils/fields/heatmaps.js';
import ListManager from '../../components/ListManager';
import {createColumnConfigs} from '../../utils/fieldUtils.js';

import './Heatmaps.scss';

const Heatmaps = props => {
	const [newHeatmap, setNewHeatmap] = useState(null);
	// const [sortBy, setSortBy] = useState('Name');
	// const [sortOrder, setSortOrder] = useState('asc');
	const [loading, setLoading] = useState(true);
	const [filterResetKey, setFilterResetKey] = useState(0);
	const editHeatmapRef = useRef(null);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const heatmaps = useSelector(state => state.heatmap).heatmaps;
	const lastUpdated = useSelector(state => state.heatmap).lastUpdated;
	const [listColumns, setListColumns] = useState([]);
	const entityType = `${constants.DISPLAY_ENTITIES.HEATMAPS}`;
		const fieldsToUse = useMemo(() => {
		return createColumnConfigs(getDisplayFields(listColumns.length === 0,listColumns))
		}, [listColumns]);

	const {
		search,
		setSearch,
		selectedFilters,
		setSelectedFilters,
		clearFilters: clearPersistentFilters
	} = usePersistentFilters('heatmaps', '', []);
			const searchedData = useMemo(() => {
		if (!heatmaps) return [];
		if (!search) return heatmaps;
		const lowerSearch = search.toLowerCase();
		return heatmaps.filter(f => f.Name.toLowerCase().includes(lowerSearch));
		}, [heatmaps, search]);

	const {
		filters,
		sortBy,
		sortOrder,
		filteredData,
		handleFilterChange,
		updateData,
		uniqueValuesByColumn,
		filterOrder
	} = useColumnFilters({
		data: searchedData || [],
		columns:fieldsToUse,
		storageKey: `heatmaps`,
		initialState: {}
	});

	const newHeatmapFooter = useMemo(() => (
		<PopupButton
			variant="primary"
			onClick={() => editHeatmapRef.current?.save()}
		>
			Create
		</PopupButton>
	), []);

	useEffect(() => {
		if(selectedFilters.length > 0) return;
		const load = async () => {
			await dispatch(heatmapActions.loadHeatmaps([]));
			setLoading(false);
		};
		load();
	}, []);

	useEffect(() => {
		if(selectedFilters.length == 0) return;
		const load = async () => {
			setLoading(true);
			await dispatch(heatmapActions.loadHeatmaps(selectedFilters));
			setLoading(false);
		};
		load();
	}, [selectedFilters]);

	useEffect(() => {
		updateData(heatmaps);
	}, [heatmaps]);

	if(loading || heatmaps == null) {
		return (
			<PageBase title="Heatmaps" removeMargins>
				<LoadingPanel />
			</PageBase>
		)
	}

	const forceReload = async (row, index) => {
		 dispatch(heatmapActions.loadHeatmaps(selectedFilters,true));
	};
	const onRowClick = async (row, index) => {
		console.log(index, row.Id);
		navigate(`/heatmap/${row.Id}`);
	};

	const updateFilter = (value) => {
		setSearch(value.toLowerCase());
	};

	// const updateSort = (sortColumn) => {
	// 	if(sortColumn === sortBy) {
	// 		setSortOrder(sortOrder === "asc" ? "desc" : "asc");
	// 	}
	// 	else {
	// 		setSortBy(sortColumn);
	// 		setSortOrder("asc");
	// 	}
	// };

	const addFilter = filter => {
		const index = selectedFilters.findIndex(f => f.name === filter.name);

		if(index < 0) {
			setSelectedFilters([...selectedFilters, filter]);
		}
		else {
			const updated = [...selectedFilters];
			updated[index] = filter;
			setSelectedFilters(updated);
		}
	};

	const removeFilter = (filterToRemove) => {
		const filters = selectedFilters.filter(f => f.name !== filterToRemove.name);
		setSelectedFilters(filters);
	};

	const onHeatmapSaved = async () => {
		setNewHeatmap(null);
		setLoading(true);
		// await dispatch(opportunityActions.loadOpportunityById(params.id))
		setLoading(false);
	};

	const onClearFilters = () => {
		clearPersistentFilters();
	};

	const filteredHeatmaps = heatmaps
		.filter(f => f.Name.toLowerCase().indexOf(search) > -1)
		.sort((a, b) =>  sortOrder === "asc" ? (a[sortBy] || '').localeCompare((b[sortBy] || '')) : (b[sortBy] || '').localeCompare((a[sortBy] || '')));

	return (
		<PageBase title="Heatmaps" removeMargins>
			<div className="heatmaps-header">
				<PopupWin
					closeOnEscape={false}
					closeOnDocumentClick={false}
					visible={newHeatmap != null}
					onClose={() => setNewHeatmap(null)}
					title="New Heatmap"
					titleStyle={{fontSize: '18px', margin: '0 0 25px 0'}}
					contentStyle={{width: '100%', height: '90%', padding: 0}}
					popupStyle={{width: '90%', maxWidth: '1600px', height: '84%', maxHeight: '800px', overflow: 'none'}}
					footerStyle={{borderTop: '1px solid #D3E1ED'}}
					footer={newHeatmapFooter}>
					{ newHeatmap && <EditHeatmap ref={editHeatmapRef} editMode={false} heatmap={newHeatmap} onClose={onHeatmapSaved} forceReload={forceReload} /> }
				</PopupWin>

				<div className="heatmaps-search-box">
					<img src={searchIcon} style={{padding: '0 10px'}} />
					<input className="heatmaps-search-input" type="text" value={search} onChange={e => updateFilter(e.target.value)} placeholder="Search Heatmaps..." />
				</div>
				<Filters availableFilters={availableFilters} clearFilters={onClearFilters} selectedFilters={selectedFilters} onAddFilter={addFilter} onRemoveFilter={removeFilter} />

				<div className="heatmaps-header-buttons">
					<ListManager
						entityType={entityType}
						displayFields={getDisplayFields()}
						defaultFields={getDisplayFields(true).map(f => f.field)}
						onColumnsChange={setListColumns}	
					/>
					<Button onClick={() => setNewHeatmap({})} type="blue" center>
						<Icon name="plus" font="AntDesign" size={16} color="white" /> Add Heatmap
					</Button>
				</div>
			</div>

			{selectedFilters.length > 0 && (
				<div style={{margin: '0 30px'}}>
					<p style={{ color: '#062229', fontWeight: 700, fontSize: 14 }}>Found {filteredData?.length || 0} Heatmaps</p>
				</div>
			)}

			<div style={{margin: '0 30px'}}>
				<Datatable 
					gridHeight="calc(100vh - 185px)"
					key={filterResetKey}
					columns={fieldsToUse}
					onRowClick={onRowClick}
					data={filteredData}
					filters={filters}
					onFilterChange={handleFilterChange}
					sortBy={sortBy}
					sortOrder={sortOrder}
					uniqueValuesByColumn={uniqueValuesByColumn}
					filterOrder={filterOrder}
				/>
			</div>
		</PageBase>
	)
};

export default Heatmaps;
